"use client"

import { useEffect, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { markAdShown } from "@/lib/ad-cooldown"
import { takeWarmedPreroll } from "@/lib/ad-preroll-cache"
import type { AdTier } from "@/lib/ad-tier"
import type { VASTResult } from "@/lib/vast-parser"

// Moved out of components/booru-detail-modal.tsx (same state and effects, same dependencies). Called right after the modal
// knows whether the post is a video; the trigger effect only touches the ad counters, so running a little earlier than
// before changes nothing (the interstitial trigger that used to run first only fires for non-video posts).
export function useVideoPreroll({
  open,
  isVideo,
  effectivePremiumUser,
  modalAdTier,
  postId,
}: {
  open: boolean
  isVideo: boolean
  effectivePremiumUser: boolean
  modalAdTier: AdTier
  postId: string | number | undefined
}) {
  const shouldShowVideoAd = useAdStore((s) => s.shouldShowVideoAd)
  const incrementVideosWatched = useAdStore((s) => s.incrementVideosWatched)
  const resetVideosWatched = useAdStore((s) => s.resetVideosWatched)
  const markFirstVideoAdShown = useAdStore((s) => s.markFirstVideoAdShown)
  const setVideoAdSettings = useAdStore((s) => s.setVideoAdSettings)
  const [showVideoPreroll, setShowVideoPreroll] = useState(false)
  const [prerollVastUrl, setPrerollVastUrl] = useState<string | null>(null)
  // ad_zones UUID for our own preroll impression/click record — the VAST
  // pixels report to the network, not to us.
  const [prerollZoneId, setPrerollZoneId] = useState<string | null>(null)
  const [prerollPreresolved, setPrerollPreresolved] = useState<{ vastUrl: string; result: VASTResult | null } | null>(null)

  // Sync video preroll frequency/skip/cooldown from admin-configured settings (cached 5min)
  useEffect(() => {
    useAdFrequencyStore.getState().fetchConfig().then(() => {
      const cfg = useAdFrequencyStore.getState().config?.video_preroll
      if (cfg) setVideoAdSettings(cfg.frequency_value, cfg.skip_after_secs, cfg.cooldown_minutes ?? 0, cfg.warmup_value)
    })
  }, [setVideoAdSettings])

  // Video pre-roll: show ad every N videos for non-premium users
  useEffect(() => {
    if (!open || !isVideo || effectivePremiumUser) return
    incrementVideosWatched()
    if (shouldShowVideoAd()) {
      // The grid warms this in the background while the user is still
      // browsing (see lib/ad-preroll-cache.ts) — if it's ready, use it and
      // skip the network round-trip entirely instead of resolving fresh.
      const warmed = takeWarmedPreroll(modalAdTier)
      if (warmed) {
        setPrerollVastUrl(warmed.vastUrl)
        setPrerollZoneId(warmed.zoneId)
        setPrerollPreresolved(warmed)
        setShowVideoPreroll(true)
        markAdShown("video_preroll")
        markFirstVideoAdShown()
        resetVideosWatched()
        return
      }
      fetch(`/api/ads/network-grid-ad?type=video&tier=${modalAdTier}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          // Only show if a real ad zone is configured — don't use fallback placeholder
          if (d?.zone_id) {
            setPrerollVastUrl(d?.vast_url ?? null)
            setPrerollZoneId(d.zone_id ?? null)
            setPrerollPreresolved(null)
            setShowVideoPreroll(true)
            markAdShown("video_preroll")
            markFirstVideoAdShown()
          }
          resetVideosWatched()
        })
        .catch(() => {
          // Don't show preroll on network error — just reset the counter
          resetVideosWatched()
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId])

  return { showVideoPreroll, setShowVideoPreroll, prerollVastUrl, prerollZoneId, prerollPreresolved }
}
